import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { Tag } from "capitalrx-components-2.0/lib/general";
import { Shelf, Spacing, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Subheading } from "capitalrx-components-2.0/lib/typography";
import { formatDate } from "capitalrx-utilities";

import { getPACaseDangerousAPI } from "@/api";
import { PACaseListResult } from "@/api/interfaces/PACase";
import { STANDARD_RESPONSIVE_LAYOUT } from "@/constants";
import { AuthenticationContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service";
import { getOverallOutcome, getThemeURL } from "@/utils";

import { PACaseRenewalBanner, PACaseRenewalTag } from "../PACaseRenewal";
import { CaseSearchForm } from "../PASearchForm";

import styles from "./PAListForm.module.scss";

export interface PAResultPanelProps {
  data: PACaseListResult;
  singleMember: boolean;
  patientDOB?: string;
  backUrl?: string;
}
type DeviceType = "desktop" | "mobile";

type ResponsiveSpacing = {
  LayoutSpacing: Record<DeviceType, Spacing>;
};

export const PAResultPanel: FC<PAResultPanelProps> = ({ data, singleMember, patientDOB, backUrl }) => {
  const [tag, setTag] = useState<JSX.Element | null>(null);
  const { getAuthToken } = useContext(AuthenticationContext);
  const RESPONSIVESPACING: ResponsiveSpacing = {
    LayoutSpacing: {
      desktop: "medium",
      mobile: "mini",
    },
  };
  const { Layout } = useResponsive(STANDARD_RESPONSIVE_LAYOUT);
  const { LayoutSpacing } = useResponsive(RESPONSIVESPACING);
  const navigate = useNavigate();

  const outcome = getOverallOutcome(data?.case_outcomes);

  useEffect(() => {
    if (data?.case.status !== "complete") {
      setTag(<Tag type="system-alternative-high-contrast">In Progress</Tag>);
    } else if (outcome === "approved") {
      setTag(<Tag type="success-high-contrast">Approved</Tag>);
    } else if (outcome === "denied") {
      setTag(<Tag type="error-high-contrast">Denied</Tag>);
    } else {
      setTag(null);
    }
  }, [data]);

  const getPACase = useCallback(
    async (case_id) => {
      try {
        // Find PA Case and process response
        const caseResponse = await getPACaseDangerousAPI({ case_id }, getAuthToken());
        const case_data: CaseSearchForm = {
          case_id: case_id,
          prescriber_npi: caseResponse.prescriber_npi,
          external_member_id: caseResponse.external_member_id,
          member_date_of_birth: patientDOB ?? caseResponse.member_date_of_birth,
        };
        const encodedParams = new URLSearchParams();
        for (const key in case_data) {
          if (case_data[key]) {
            encodedParams.append(key, case_data[key]);
          }
        }
        if (backUrl) {
          encodedParams.append("back_url", backUrl);
        }
        navigate({
          pathname: getThemeURL("PA_CASE"),
          search: encodedParams.toString(),
        });

        // analytics log case found
        logEvent(ANALYTIC_EVENTS.PA_CASE_SEARCH.found);
      } catch (error) {
        console.error(error);
      }
    },
    [backUrl, data, getAuthToken, navigate, patientDOB],
  );

  return (
    <div className={styles.ResultPanel}>
      <Stack spacing="small">
        <button
          type="button"
          className={styles.ResultPanelButton}
          onClick={() => {
            getPACase(data.case.case_id);
          }}
        >
          <Stack spacing="small">
            <Shelf alignment="top" spacing="mini">
              <Layout spacing={LayoutSpacing}>
                {singleMember ? (
                  <Subheading>Case ID {data.case.case_id}</Subheading>
                ) : (
                  <Subheading>
                    {data.case.member_last_name}, {data.case.member_first_name}
                  </Subheading>
                )}
                {data?.case?.queue?.toLowerCase() === "outreach" && <Tag type="warning-high-contrast">Action Required</Tag>}
              </Layout>
              <Shelf.Spacer />
              {tag}
              <PACaseRenewalTag
                expirationDate={data.case.expiration_date}
                renewalWindowDate={data.case.renewal_window_date}
                hasRenewalCode={data.case.has_renewal_code}
                status={data.case.status}
              />
            </Shelf>
            <Layout spacing={LayoutSpacing}>
              {!singleMember && <Body>CASE ID {data.case.case_id}</Body>}
              <Body>{data.case.drug_name}</Body>
              {outcome === "approved" && (
                <Body>
                  {formatDate(data?.case?.effective_date, "MM/DD/YYYY")}-{formatDate(data?.case?.expiration_date, "MM/DD/YYYY")}
                </Body>
              )}
              <Shelf.Spacer />
              {data?.case?.date_of_service && <Body size="small">Submitted {formatDate(data.case.date_of_service, "MM/DD/YYYY")}</Body>}
            </Layout>
          </Stack>
        </button>
        {data.case.is_complete === true && outcome === "approved" && (
          <PACaseRenewalBanner
            caseId={data.case.case_id}
            expirationDate={data.case.expiration_date}
            renewalWindowDate={data.case.renewal_window_date}
            hasRenewalCode={data.case.has_renewal_code}
            status={data.case.status}
          />
        )}
      </Stack>
    </div>
  );
};

export default PAResultPanel;
